// Central registry of all 16 transactional templates.
// Add a row here when introducing a new template.

import {
  renderBookingConfirmation,
  renderAutoCancellation,
  renderPostEventRecap,
} from './templates-organizer.ts';
import {
  renderInvite,
  renderRsvpConfirmation,
  renderEventCancelledInvitee,
  renderDayOfReminder,
  renderRsvpNudge24h,
  renderRsvpNudge12pm,
} from './templates-invitee.ts';
import {
  renderVendorBookingNotification,
  renderVendorHeadcountLock,
  renderVendorBookingCancelled,
  renderVendorWeeklySummary,
} from './templates-vendor.ts';
import {
  renderMagicLink,
  renderWelcome,
  renderVendorTeamInvite,
  renderVendorWelcome,
} from './templates-account.ts';
import type { RenderedEmail } from './types.ts';

export type TemplateAudience = 'organizer' | 'invitee' | 'vendor' | 'account';
export type TemplateTrigger = 'event' | 'cron' | 'manual';

export type TemplateMeta = {
  slug: string;             // 'booking_confirmation' — stable identifier
  number: string;           // '01' — display order from the spec
  audience: TemplateAudience;
  title: string;            // short human label
  description: string;      // one-line "why" from the spec
  trigger: TemplateTrigger;
  triggerLabel: string;     // 'checkout.completed', 'cron · T+24h', etc.
  fromName: string;         // default From: name
  fromAddress: string;      // default From: email
  render: (data: any) => RenderedEmail;
};

export const TEMPLATES: TemplateMeta[] = [
  // ─── Organizer ──────────────────────────────────────────────────────────
  {
    slug: 'booking_confirmation',
    number: '01',
    audience: 'organizer',
    title: 'Booking confirmation',
    description: 'Receipt + calendar + manage-booking link',
    trigger: 'event',
    triggerLabel: 'checkout.completed',
    fromName: 'Goaround',
    fromAddress: 'hello@goaround.io',
    render: renderBookingConfirmation,
  },
  {
    slug: 'auto_cancellation',
    number: '02',
    audience: 'organizer',
    title: 'Auto-cancellation notice',
    description: 'Below-floor refund + reschedule',
    trigger: 'event',
    triggerLabel: 'booking.auto_cancelled',
    fromName: 'Goaround',
    fromAddress: 'hello@goaround.io',
    render: renderAutoCancellation,
  },
  {
    slug: 'post_event_recap',
    number: '03',
    audience: 'organizer',
    title: 'Post-event recap + review',
    description: '"How\'d it go?" · drives review flywheel',
    trigger: 'cron',
    triggerLabel: 'cron · T+24h after event_end',
    fromName: 'Goaround',
    fromAddress: 'reviews@goaround.io',
    render: renderPostEventRecap,
  },

  // ─── Invitee ────────────────────────────────────────────────────────────
  {
    slug: 'invite',
    number: '04',
    audience: 'invitee',
    title: 'Invite with RSVP buttons',
    description: 'The headline send · ga-send-invite',
    trigger: 'event',
    triggerLabel: 'organizer.invite_sent',
    fromName: 'Goaround',
    fromAddress: 'invites@goaround.io',
    render: renderInvite,
  },
  {
    slug: 'rsvp_confirmation',
    number: '05',
    audience: 'invitee',
    title: 'RSVP confirmation',
    description: '"You\'re in." minimalist receipt',
    trigger: 'event',
    triggerLabel: 'invitee.rsvp_yes',
    fromName: 'Goaround',
    fromAddress: 'rsvp@goaround.io',
    render: renderRsvpConfirmation,
  },
  {
    slug: 'event_cancelled_invitee',
    number: '06',
    audience: 'invitee',
    title: 'Event cancelled (invitee)',
    description: 'Critical · prevents trust-shattering no-shows',
    trigger: 'event',
    triggerLabel: 'booking.cancelled → all yes/maybe invitees',
    fromName: 'Goaround',
    fromAddress: 'rsvp@goaround.io',
    render: renderEventCancelledInvitee,
  },
  {
    slug: 'day_of_reminder',
    number: '07',
    audience: 'invitee',
    title: 'Day-of reminder · T-3h',
    description: 'Address + who else is coming',
    trigger: 'cron',
    triggerLabel: 'cron · T-3h, status=yes',
    fromName: 'Goaround',
    fromAddress: 'reminders@goaround.io',
    render: renderDayOfReminder,
  },
  {
    slug: 'rsvp_nudge_24h',
    number: '08',
    audience: 'invitee',
    title: 'RSVP nudge · 24h prior',
    description: 'Polite — "we still need to hear back"',
    trigger: 'cron',
    triggerLabel: 'cron · T-24h, status ∈ {maybe, none}',
    fromName: 'Goaround',
    fromAddress: 'rsvp@goaround.io',
    render: renderRsvpNudge24h,
  },
  {
    slug: 'rsvp_nudge_12pm',
    number: '09',
    audience: 'invitee',
    title: 'RSVP nudge · 12 PM day-of',
    description: 'Last-call energy · different copy from #08',
    trigger: 'cron',
    triggerLabel: 'cron · day-of 12:00, status ∈ {maybe, none}',
    fromName: 'Goaround',
    fromAddress: 'rsvp@goaround.io',
    render: renderRsvpNudge12pm,
  },

  // ─── Vendor ─────────────────────────────────────────────────────────────
  {
    slug: 'vendor_booking_notification',
    number: '10',
    audience: 'vendor',
    title: 'Booking notification',
    description: '"You booked a thing. Here\'s what we know."',
    trigger: 'event',
    triggerLabel: 'checkout.completed → vendor primary contact',
    fromName: 'Goaround',
    fromAddress: 'vendors@goaround.io',
    render: renderVendorBookingNotification,
  },
  {
    slug: 'vendor_headcount_lock',
    number: '11',
    audience: 'vendor',
    title: '3 PM headcount lock + payment',
    description: 'Day-of "what\'s actually happening tonight"',
    trigger: 'cron',
    triggerLabel: 'cron · day-of 15:00',
    fromName: 'Goaround',
    fromAddress: 'vendors@goaround.io',
    render: renderVendorHeadcountLock,
  },
  {
    slug: 'vendor_booking_cancelled',
    number: '12',
    audience: 'vendor',
    title: 'Booking cancelled (vendor)',
    description: 'Free the slot · avoid awkward "we set up for 12"',
    trigger: 'event',
    triggerLabel: 'booking.cancelled → vendor',
    fromName: 'Goaround',
    fromAddress: 'vendors@goaround.io',
    render: renderVendorBookingCancelled,
  },
  {
    slug: 'vendor_weekly_summary',
    number: '13',
    audience: 'vendor',
    title: 'Weekly performance summary',
    description: 'Mon 9 AM cron · retention driver',
    trigger: 'cron',
    triggerLabel: 'cron · Mon 09:00, vendors.active',
    fromName: 'Goaround',
    fromAddress: 'reports@goaround.io',
    render: renderVendorWeeklySummary,
  },

  // ─── Account ────────────────────────────────────────────────────────────
  {
    slug: 'magic_link',
    number: '14',
    audience: 'account',
    title: 'Magic-link sign-in',
    description: 'Style only · Supabase handles the auth',
    trigger: 'event',
    triggerLabel: 'auth.signin_requested',
    fromName: 'Goaround',
    fromAddress: 'auth@goaround.io',
    render: renderMagicLink,
  },
  {
    slug: 'welcome',
    number: '15',
    audience: 'account',
    title: 'Welcome email',
    description: '60-second product orientation',
    trigger: 'event',
    triggerLabel: 'user.first_signin',
    fromName: 'Maya at Goaround',
    fromAddress: 'hello@goaround.io',
    render: renderWelcome,
  },
  {
    slug: 'vendor_team_invite',
    number: '16',
    audience: 'account',
    title: 'Vendor team-member invite',
    description: '"You were added to Snafu Bar\'s Goaround"',
    trigger: 'event',
    triggerLabel: 'ga_vendor_members.insert → invited member',
    fromName: 'Goaround',
    fromAddress: 'vendors@goaround.io',
    render: renderVendorTeamInvite,
  },
  {
    slug: 'vendor_welcome',
    number: '17',
    audience: 'account',
    title: 'Vendor welcome',
    description: 'Sent when /onboarding step 1 creates the vendor account',
    trigger: 'event',
    triggerLabel: 'user.first_signin (role = vendor)',
    fromName: 'Maya at Goaround',
    fromAddress: 'vendors@goaround.io',
    render: renderVendorWelcome,
  },
];

export const TEMPLATE_BY_SLUG: Record<string, TemplateMeta> = Object.fromEntries(
  TEMPLATES.map((t) => [t.slug, t]),
);
